import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import {
  ReactDefaultProps,
  ReactComponentStaticProps,
} from "../../types/react";

import { useUniqueId } from "../hooks/useUniqueId";
import { useMediaQuery, breakpoints } from "../hooks/useMediaQuery";

import { ComparisonTableBody } from "./Body";
import { ComparisonTableCol } from "./Col";
import { ComparisonTableCollapsible } from "./Collapsible";
import { ComparisonTableColHeader } from "./ColHeader";
import { ComparisonTableColors } from "./Colors";
import { ComparisonTableFeatureBoolean } from "./FeatureBoolean";
import { ComparisonTableGroup } from "./Group";
import { ComparisonTableHead } from "./Head";
import { ComparisonTableHeadColHeader } from "./HeadColHeader";
import { ComparisonTableHeadline } from "./Headline";
import { ComparisonTableImage } from "./Image";
import { ComparisonTableRow } from "./Row";
import { ComparisonTableRowHeader } from "./RowHeader";
import { ComparisonTableRowHeaderSubline } from "./RowHeaderSubline";
import { ComparisonTableShowMore } from "./ShowMore";
import { ComparisonTableSliderButton } from "./SliderButton";

import { TableProvider } from "./TableContext";

import styles from "./ComparisonTable.scss";

const columnsPerViewportValues = [1, 2, 3, 4, 5] as const;
type ColumnsPerViewportValue = typeof columnsPerViewportValues[number];

type ColumnPerViewport = Readonly<
  [
    // [S, M, L, XL]
    ColumnsPerViewportValue,
    ColumnsPerViewportValue,
    ColumnsPerViewportValue,
    ColumnsPerViewportValue
  ]
>;

const arrowPositionVariantValues = [
  "hardware",
  "hardware-dense",
  "product",
  "product-hero-feature",
] as const;

type ArrowPositionVariantValue = typeof arrowPositionVariantValues[number];

type ComparisonTableBaseProps = {
  id?: string;
  columnCount: number;
  hasColumnHeader?: boolean;
  columnsPerViewport?: ColumnPerViewport;
  arrowPositionVariant: ArrowPositionVariantValue;
};

export type ComparisonTableProps = ComparisonTableBaseProps &
  Omit<React.ComponentPropsWithoutRef<"table">, keyof ComparisonTableBaseProps>;

const defaultProps = {
  id: "",
  hasColumnHeader: false,
  columnsPerViewport: [2, 3, 4, 4],
} as const;

const validatedDefaultProps: ReactDefaultProps<
  ComparisonTableBaseProps,
  typeof defaultProps
> = defaultProps;

const CONTAINER_PADDING_S = 12;
const CONTAINER_PADDING_M_PLUS = 24;

let handlingWheel: boolean;
let wheelRefresh: boolean;
let scrollRefresh: boolean;
let scrollTimer: number | undefined;

export const ComparisonTable = (props: ComparisonTableProps): JSX.Element => {
  const {
    children,
    id,
    hasColumnHeader = defaultProps.hasColumnHeader,
    columnsPerViewport = defaultProps.columnsPerViewport,
    columnCount,
    arrowPositionVariant,
    ...restProps
  } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollIndex, _setScrollIndex] = useState(0);
  const scrollIndexRef = useRef(scrollIndex);
  const setScrollIndex = (value: number) => {
    scrollIndexRef.current = value;
    _setScrollIndex(value);
  };
  const [initialX, setInitialX] = useState(0);
  const [lastTouchXPosition, setLastTouchXPosition] = useState(0);
  const [initialScrollPosition, setInitialScrollPosition] = useState(0);
  const [showSliderButtons, setShowSliderButtons] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const viewports = {
    S: useMediaQuery(`(max-width: ${breakpoints.m - 1}px)`),
    M: useMediaQuery(
      `(min-width: ${breakpoints.m}px) and (max-width: ${breakpoints.l - 1}px)`
    ),
    L: useMediaQuery(
      `(min-width: ${breakpoints.l}px) and (max-width: ${breakpoints.xl - 1}px)`
    ),
    XL: useMediaQuery(`(min-width: ${breakpoints.xl}px)`),
  };
  const isViewportRef = useRef(viewports);
  isViewportRef.current = viewports;

  function getContainerPaddingInCurrentViewport(): number {
    if (isViewportRef.current.S) return CONTAINER_PADDING_S;

    return CONTAINER_PADDING_M_PLUS;
  }

  function getColumnCountInCurrentView(): number {
    const [s, m, l, xl] = columnsPerViewport;
    if (isViewportRef.current.S) return s;
    if (isViewportRef.current.M) return m;
    if (isViewportRef.current.L) return l;
    if (isViewportRef.current.XL) return xl;

    return 0;
  }

  const hasOnlyOneVisibleContentColumn = () =>
    hasColumnHeader && getColumnCountInCurrentView() === 2;

  function getNumberOfPagesInCurrentView(): number {
    const correctedColumnCount = hasColumnHeader
      ? columnCount - 1
      : columnCount;
    if (hasOnlyOneVisibleContentColumn()) {
      return (
        Math.floor(columnCount - getColumnCountInCurrentView()) +
        (hasColumnHeader ? 1 : 2)
      );
    }

    return Math.ceil(correctedColumnCount / getColumnCountInCurrentView());
  }

  function goPrev() {
    if (scrollIndexRef.current > 0) {
      setScrollIndex(scrollIndexRef.current - 1);
      return true;
    }
    return false;
  }

  function goNext() {
    if (scrollIndexRef.current < getNumberOfPagesInCurrentView() - 1) {
      setScrollIndex(scrollIndexRef.current + 1);
      return true;
    }
    return false;
  }

  function scrollToIndex(index: number) {
    if (scrollRef.current && typeof scrollRef.current.scroll === "function") {
      const { scrollWidth } = scrollRef.current;

      scrollRef.current.scroll({
        top: 0,
        left:
          ((index * (scrollWidth - getContainerPaddingInCurrentViewport())) /
            columnCount) *
          (hasOnlyOneVisibleContentColumn()
            ? 1
            : getColumnCountInCurrentView()),
        behavior: "smooth",
      });
    }
  }

  function updateSliderButtons() {
    if (!scrollRef.current) {
      return;
    }
    const newShowSliderbutttons =
      scrollRef.current.scrollWidth !== scrollRef.current.clientWidth;
    if (showSliderButtons !== newShowSliderbutttons) {
      setShowSliderButtons(newShowSliderbutttons);
    }
  }

  function handleResize() {
    if (!scrollRef.current) {
      return;
    }
    updateSliderButtons();
    scrollToIndex(scrollIndexRef.current);
  }

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  });

  useEffect(() => {
    scrollToIndex(scrollIndexRef.current);
  }, [scrollIndexRef.current]);

  function handleMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    setIsDragging(true);
    setInitialX(e.clientX);
    if (scrollRef.current && typeof scrollRef.current.scrollLeft === "number") {
      setInitialScrollPosition(scrollRef.current.scrollLeft);
    }
  }

  function handleMouseUp(e: React.MouseEvent<HTMLDivElement>) {
    setIsDragging(false);
    const mouseMovementXDistance = initialX - e.clientX;
    if (mouseMovementXDistance < -12) {
      goPrev();
    } else if (mouseMovementXDistance > 12) {
      goNext();
    } else if (
      scrollRef.current &&
      typeof scrollRef.current.scroll === "function"
    ) {
      scrollRef.current.scroll({
        top: 0,
        left: initialScrollPosition,
        behavior: "smooth",
      });
    }
  }

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (isDragging) {
      if (
        scrollRef.current &&
        typeof scrollRef.current.scrollLeft === "number"
      ) {
        scrollRef.current.scrollLeft =
          initialScrollPosition + (initialX - e.clientX);
      } else if (
        scrollRef.current &&
        typeof scrollRef.current.scroll === "function"
      ) {
        scrollRef.current.scroll({
          top: 0,
          left: initialScrollPosition,
          behavior: "smooth",
        });
      }
    }
  }

  function handleTouchDown(e: React.TouchEvent<HTMLDivElement>) {
    setIsDragging(true);
    setInitialX(e.touches.item(0).clientX);
    if (scrollRef.current) {
      setInitialScrollPosition(scrollRef.current.scrollLeft);
    }
  }

  function handleTouchUp() {
    setIsDragging(false);
    const TouchMovementXDistance = initialX - lastTouchXPosition;
    if (TouchMovementXDistance < -12) {
      goPrev();
    } else if (TouchMovementXDistance > 12) {
      goNext();
    } else if (
      scrollRef.current &&
      typeof scrollRef.current.scroll === "function"
    ) {
      scrollRef.current.scroll({
        top: 0,
        left: initialScrollPosition,
        behavior: "smooth",
      });
    }
  }

  function handleTouchMove(e: React.TouchEvent<HTMLDivElement>) {
    if (isDragging) {
      setLastTouchXPosition(e.touches.item(0).clientX);
      if (scrollRef.current) {
        scrollRef.current.scrollLeft =
          initialScrollPosition + (initialX - e.touches.item(0).clientX);
      }
    }
  }

  function wheelHandler(this: HTMLDivElement, event: WheelEvent) {
    if (scrollRef.current) {
      const { deltaX } = event;
      if (Math.abs(deltaX) < 2) {
        return;
      }
      if (handlingWheel) {
        return;
      }
      if (wheelRefresh) {
        return;
      }
      wheelRefresh = true;
      window.requestAnimationFrame(() => {
        handlingWheel = true;
        if (deltaX > 0) {
          if (!goNext()) {
            handlingWheel = false;
          }
        }
        if (deltaX < 0) {
          if (!goPrev()) {
            handlingWheel = false;
          }
        }
        wheelRefresh = false;
      });
    }
  }

  function handleScroll(event: React.UIEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (scrollRefresh) {
      return;
    }
    scrollRefresh = true;
    requestAnimationFrame(() => {
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
      scrollTimer = setTimeout(() => {
        handlingWheel = false;
        scrollTimer = undefined;
      }, 500);
      scrollRefresh = false;
    });
  }

  useEffect(() => {
    scrollRef.current?.addEventListener("wheel", wheelHandler, {
      passive: false,
    });
    return () => scrollRef.current?.removeEventListener("wheel", wheelHandler);
  }, [scrollRef.current]);

  const ariaId =
    id ||
    `comparison-table-${
      useUniqueId<ComparisonTableProps>("Input", {
        ...props,
        children: <TableProvider>{children}</TableProvider>,
      }) + 1
    }`;
  const colSpacingStyleS = `col-spacing-s-${columnsPerViewport[0]}` as const;
  const colSpacingStyleM = `col-spacing-m-${columnsPerViewport[1]}` as const;
  const colSpacingStyleL = `col-spacing-l-${columnsPerViewport[2]}` as const;
  const colSpacingStyleXL = `col-spacing-xl-${columnsPerViewport[3]}` as const;

  updateSliderButtons();

  return (
    <div className={styles.wrapper}>
      <ComparisonTableSliderButton
        onClick={goPrev}
        isHidden={!showSliderButtons || scrollIndex === 0}
        positionVariant={arrowPositionVariant}
      />
      <ComparisonTableSliderButton
        direction="right"
        onClick={goNext}
        isHidden={
          !showSliderButtons ||
          scrollIndex === getNumberOfPagesInCurrentView() - 1
        }
        positionVariant={arrowPositionVariant}
      />
      <div
        role="scrollbar"
        id={ariaId}
        aria-controls={ariaId}
        aria-valuenow={0}
        tabIndex={0}
        className={styles.scroller}
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchDown}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchUp}
        onScroll={handleScroll}
        // prevent scrolling via arrow keys
        onKeyDown={(e) => e.preventDefault()}
      >
        <TableProvider hasColumnHeader={hasColumnHeader}>
          <table
            className={cx(
              styles.table,
              styles[colSpacingStyleS],
              styles[colSpacingStyleM],
              styles[colSpacingStyleL],
              styles[colSpacingStyleXL],
              {
                [styles["with-col-header"]]: hasColumnHeader,
              }
            )}
            {...restProps}
          >
            {children}
          </table>
        </TableProvider>
      </div>
      <div
        className={styles["show-more-portal"]}
        id="show-more-portal-target"
      />
    </div>
  );
};

ComparisonTable.Body = ComparisonTableBody;
ComparisonTable.Col = ComparisonTableCol;
ComparisonTable.Collapsible = ComparisonTableCollapsible;
ComparisonTable.Colors = ComparisonTableColors;
ComparisonTable.ColHeader = ComparisonTableColHeader;
ComparisonTable.Group = ComparisonTableGroup;
ComparisonTable.Head = ComparisonTableHead;
ComparisonTable.HeadColHeader = ComparisonTableHeadColHeader;
ComparisonTable.Headline = ComparisonTableHeadline;
ComparisonTable.Image = ComparisonTableImage;
ComparisonTable.Row = ComparisonTableRow;
ComparisonTable.RowHeader = ComparisonTableRowHeader;
ComparisonTable.RowHeaderSubline = ComparisonTableRowHeaderSubline;
ComparisonTable.ShowMore = ComparisonTableShowMore;
ComparisonTable.FeatureBoolean = ComparisonTableFeatureBoolean;

const propTypes: ReactComponentStaticProps<
  ComparisonTableProps
>["propTypes"] = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  columnCount: PropTypes.number.isRequired,
  hasColumnHeader: PropTypes.bool,
  columnsPerViewport: PropTypes.arrayOf(
    PropTypes.oneOf(columnsPerViewportValues).isRequired
  ) as React.Validator<ColumnPerViewport>,
  arrowPositionVariant: PropTypes.oneOf(arrowPositionVariantValues).isRequired,
} as const;

ComparisonTable.propTypes = propTypes;
ComparisonTable.defaultProps = validatedDefaultProps;

const displayName: ReactComponentStaticProps<
  ComparisonTableProps
>["displayName"] = "ComparisonTable";

ComparisonTable.displayName = displayName;

export default ComparisonTable;
